// Transient status messages, in one place.
//
// This replaces two things:
//
//   * `alert()`, which blocks the whole tab, cannot be styled, and on failure
//     paths left the user staring at browser chrome that says "localhost:6060
//     says:";
//   * Popups/Delete_File_Popup.svelte -- a full-width green bar pushed into the
//     page above the content, backed by its own store. It shifted the layout
//     when it appeared, and said "Deleting scenario" in an app that has no
//     scenarios.
//
// A pending toast is the "please wait" case: create it with {persist: true},
// hold onto the id, and resolve it when the work finishes.

import { writable } from "svelte/store";

/** @type {import("svelte/store").Writable<Array<object>>} */
export const toasts = writable([]);

let nextId = 1;

const DEFAULT_DURATION = 4000;

/**
 * @param {string} message
 * @param {object} [options]
 * @param {"info"|"success"|"error"|"pending"} [options.tone]
 * @param {boolean} [options.persist] - stay until dismissed explicitly.
 *   Implied by tone "pending": a spinner that times out is a lie.
 * @param {number} [options.duration]
 * @returns {number} id, for dismissToast / resolveToast
 */
export function toast(message, options = {}) {
  const { tone = "info", duration = DEFAULT_DURATION } = options;
  const persist = options.persist ?? tone === "pending";
  const id = nextId++;

  toasts.update((list) => [...list, { id, message, tone }]);

  if (!persist) {
    setTimeout(() => dismissToast(id), duration);
  }

  return id;
}

export function dismissToast(id) {
  toasts.update((list) => list.filter((t) => t.id !== id));
}

/**
 * Swaps a pending toast for its outcome. Used on every "…please wait" path so
 * the spinner cannot outlive the request that created it.
 */
export function resolveToast(id, message, tone = "success") {
  dismissToast(id);
  return toast(message, { tone });
}
